import {
	type DefaultMessagesResponse,
	type RML,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { useInitialData } from '@post-office/message-maker-browser/use-initial-data';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { Stack } from '@atlaskit/primitives/stack';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { useEffect, useState } from 'react';

import { type AppProps } from './types';

export {
	ConnectorSources,
	OAuthSource,
	type OAuthSource as OAuthSourceType,
	type ConnectorProps,
} from '../common/types';

export type PlacementRenderProps = AppProps & {
	/** Called by message template to dismiss itself from the UI */
	dismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'search-page-results-inline',
	options: {
		choreographer: {
			isDefaultEnabled: true, // You almost always will want this option to be true. See below for an explanation
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps & RML
> = createFrontend<AppProps & RML>((Messages) => ({ initialData, ...placementProps }) => {
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);
	const apiResponse = useInitialData<DefaultMessagesResponse>(initialData);

	useEffect(() => {
		const { error, data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}

		setMessages(data?.messages ?? []);
	}, [apiResponse]);

	const handleDismiss = (messageInstanceId: string) => {
		setMessages((prev) => prev.filter((m) => m.messageInstanceId !== messageInstanceId));
	};

	if (messages.length === 0) {
		return null;
	}

	return (
		<Stack space="space.200">
			{messages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					{...placementProps}
					{...message}
					dismiss={() => handleDismiss(message.messageInstanceId)}
				/>
			))}
		</Stack>
	);
});

export type PlacementFrontendProps = AppProps & RML;
